/* eslint-disable @typescript-eslint/no-unsafe-return */
import { BM, } from './backend';
import { CurrentWindowBackendContext } from './backend/current_window_backend_context';
import { EmptyBackendContext } from './backend/empty_backend';
import { EmptyComposedBackendContext } from './backend/empty_composed_backend';
import { ClassList, StyleScopeManager } from './class_list';
import { METHOD_TAG, } from './component_params';
import { getDefaultComponentSpace } from './component_space';
import { parseMultiPaths, parseSinglePath } from './data_path';
import { DataGroup, getDeepCopyStrategy, } from './data_proxy';
import { simpleDeepCopy } from './data_utils';
import { performanceMeasureEnd, performanceMeasureRenderWaterfall, performanceMeasureStart, } from './dev_tools';
import { Element } from './element';
import { FuncArr, safeCallback } from './func_arr';
import { ENV, globalOptions, normalizeComponentOptions, } from './global_options';
import { MutationObserverTarget } from './mutation_observer';
import { Relation, generateRelationDefinitionGroup, normalizeRelation, } from './relation';
import { ShadowRoot } from './shadow_root';
import { getDefaultTemplateEngine } from './tmpl';
import { TraitBehavior, TraitGroup } from './trait_behaviors';
import { COMPONENT_SYMBOL, isComponent, isElement } from './type_symbol';
import { ThirdError, dispatchError, triggerWarning } from './warning';
export const convertGenerics = (compDef, sourceBehavior, owner, genericTargets) => {
    const space = sourceBehavior.ownerSpace;
    const hostUsing = owner === null || owner === void 0 ? void 0 : owner._$behavior._$using;
    const hostGenericImpls = owner === null || owner === void 0 ? void 0 : owner._$genericImpls;
    if (!compDef._$detail)
        compDef.prepare();
    const childBeh = compDef.behavior;
    let genericImpls;
    const generics = childBeh._$generics;
    if (generics) {
        const genericDefaults = childBeh._$genericDefaults;
        genericImpls = Object.create(null);
        for (let i = 0; i < generics.length; i += 1) {
            const key = generics[i];
            if (genericTargets && Object.prototype.hasOwnProperty.call(genericTargets, key)) {
                const target = genericTargets[key];
                if (typeof target === 'string') {
                    if (hostUsing === null || hostUsing === void 0 ? void 0 : hostUsing[target]) {
                        genericImpls[key] = hostUsing[target];
                    }
                    else if (hostGenericImpls && hostGenericImpls[target]) {
                        genericImpls[key] = hostGenericImpls[target];
                    }
                    else {
                        const comp = space.getGlobalUsingComponent(target);
                        if (typeof comp === 'string') {
                            genericImpls[key] = comp;
                        }
                        else if (comp) {
                            genericImpls[key] = {
                                final: comp,
                                source: sourceBehavior,
                                placeholder: null,
                                waiting: null,
                            };
                        }
                        else {
                            triggerWarning(`Generic "${key}" value "${target}" is not valid`, compDef.is);
                            const defaultComp = space.getDefaultComponent();
                            if (!defaultComp) {
                                throw new ThirdError(`Cannot find default component for generic "${key}"`, '[prepare]', compDef.is);
                            }
                            genericImpls[key] = {
                                final: defaultComp,
                                source: sourceBehavior,
                                placeholder: null,
                                waiting: null,
                            };
                        }
                    }
                }
                else {
                    genericImpls[key] = target;
                }
            }
            else {
                let defaultComp = genericDefaults[key];
                if (!defaultComp) {
                    defaultComp = space.getDefaultComponent();
                    if (!defaultComp) {
                        throw new ThirdError(`Cannot find default component for generic "${key}"`, '[prepare]', compDef.is);
                    }
                    else {
                        triggerWarning(`No component specified for generic "${key}", using default component (on component "${compDef.is}")`);
                    }
                }
                genericImpls[key] =
                    typeof defaultComp === 'string'
                        ? defaultComp
                        : {
                            final: defaultComp,
                            source: sourceBehavior,
                            placeholder: null,
                            waiting: null,
                        };
            }
        }
    }
    else {
        genericImpls = null;
    }
    return genericImpls;
};
export const resolvePlaceholder = (placeholder, space, behavior, genericImpls) => {
    const using = behavior._$using;
    const usingTarget = using[placeholder] || (genericImpls && genericImpls[placeholder]);
    let ret = null;
    if (usingTarget) {
        if (typeof usingTarget === 'string') {
            ret = usingTarget;
        }
        else if (usingTarget.placeholder === null) {
            ret = usingTarget.final;
        }
        else {
            triggerWarning(`Placeholder on generic implementation is not valid`, behavior.is);
        }
    }
    if (ret)
        return ret;
    let comp = space.getGlobalUsingComponent(placeholder);
    if (comp === null && space._$allowUnusedNativeNode && placeholder !== '') {
        comp = placeholder;
    }
    if (!comp) {
        comp = space.getDefaultComponent();
        if (!comp) {
            throw new ThirdError(`Cannot find placeholder target "${placeholder}"`, '[prepare]', behavior.is);
        }
        triggerWarning(`Cannot find placeholder target "${placeholder}", using default component.`, behavior.is);
    }
    return comp;
};
export class ComponentDefinition {
    /** @internal */
    constructor(behavior) {
        this.behavior = behavior;
        this.is = this.behavior.is;
        this._$detail = null;
        this._$options = normalizeComponentOptions(behavior._$options, behavior.ownerSpace.getComponentOptions());
        const templateEngine = this._$options.templateEngine;
        this._$templateEngine = templateEngine !== null && templateEngine !== void 0 ? templateEngine : getDefaultTemplateEngine();
    }
    general() {
        return this;
    }
    /** Get the normalized component options */
    getComponentOptions() {
        return this._$options;
    }
    /**
     * List all component dependencies (recursively)
     *
     * This method will prepare the underlying behavior.
     * The caller component is not included in the result.
     */
    getComponentDependencies() {
        return this.behavior.getComponentDependencies();
    }
    /**
     * Update the template field
     *
     * This method throws error if the template engine does not support template update.
     */
    updateTemplate(template) {
        var _a;
        if (!((_a = this._$detail) === null || _a === void 0 ? void 0 : _a.template.updateTemplate)) {
            throw new Error(`The template engine of component "${this.is}" does not support template update`);
        }
        this.behavior._$updateTemplate(template);
        this._$detail.template.updateTemplate(this.behavior);
    }
    isPrepared() {
        return !!this._$detail;
    }
    prepare() {
        if (this._$detail)
            return;
        this.behavior.prepare();
        const behavior = this.behavior;
        const options = this._$options;
        const propSetters = {};
        // add properties to component prototype if needed
        if (options.writeFieldsToNode) {
            const keys = Object.keys(behavior._$propertyMap);
            for (let i = 0; i < keys.length; i += 1) {
                const propName = keys[i];
                propSetters[propName] = {
                    enumerable: true,
                    get() {
                        return this._$dataGroup.data[propName];
                    },
                    set(v) {
                        const dataGroup = this._$dataGroup;
                        dataGroup.replaceDataOnPath([propName], v);
                        dataGroup.applyDataUpdates();
                    },
                };
            }
            const methodKeys = Object.keys(behavior._$methodMap);
            for (let i = 0; i < methodKeys.length; i += 1) {
                const methodName = methodKeys[i];
                propSetters[methodName] = {
                    enumerable: true,
                    value: behavior._$methodMap[methodName],
                };
            }
        }
        // create prototype
        const protoFunc = function ComponentInst() {
            /* a component */
        };
        propSetters.constructor = {
            value: protoFunc,
            writable: true,
        };
        protoFunc.prototype = Object.create(Component.prototype, propSetters);
        const proto = protoFunc.prototype;
        proto._$behavior = behavior;
        proto._$definition = this;
        proto._$methodMap = behavior._$methodMap;
        // init other helpers
        proto._$dataGroupObserverTree = behavior._$generateObserverTree();
        proto._$lifetimeFuncs = behavior._$getAllLifetimeFuncs();
        proto._$pageLifetimeFuncs = behavior._$getAllPageLifetimeFuncs();
        const relationDefinitionGroup = generateRelationDefinitionGroup(behavior._$relationMap);
        const dataDeepCopy = getDeepCopyStrategy(options.dataDeepCopy);
        const propertyPassingDeepCopy = getDeepCopyStrategy(options.propertyPassingDeepCopy);
        // call template engine
        const template = this._$templateEngine.create(behavior, options);
        this._$detail = {
            proto,
            template,
            dataDeepCopy,
            propertyPassingDeepCopy,
            relationDefinitionGroup,
        };
    }
}
let componentInstanceIdInc = 1;
let defaultBackendContext = null;
export const getDefaultBackendContext = () => {
    if (defaultBackendContext)
        return defaultBackendContext;
    let c;
    if (BM.DOMLIKE) {
        c = new CurrentWindowBackendContext();
    }
    else if (BM.COMPOSED) {
        c = new EmptyComposedBackendContext();
    }
    else {
        c = new EmptyBackendContext();
    }
    defaultBackendContext = c;
    return c;
};
/**
 * A node that has a shadow tree attached to it
 */
export class Component extends Element {
    /* istanbul ignore next */
    constructor() {
        throw new Error('Element cannot be constructed directly');
        // eslint-disable-next-line no-unreachable
        super();
    }
    general() {
        return this;
    }
    /**
     * Cast a general component node to the instance of the specified component
     *
     * Returns `null` if the component node is not the instance of the specified component.
     */
    asInstanceOf(componentDefinition) {
        if (this._$behavior !== componentDefinition.behavior) {
            return null;
        }
        return this;
    }
    static register(def, space) {
        return (space || getDefaultComponentSpace()).defineComponent(def);
    }
    /** @internal */
    static _$tagMethod(func) {
        const taggedMethod = func;
        taggedMethod[METHOD_TAG] = true;
        return taggedMethod;
    }
    static isTaggedMethod(func) {
        return typeof func === 'function' && !!func[METHOD_TAG];
    }
    static _$advancedCreate(tagName, def, owner, backendContext, genericImpls, initPropValues) {
        var _a, _b;
        if (!def._$detail) {
            if (ENV.DEV)
                performanceMeasureStart('component.prepare');
            def.prepare();
            if (ENV.DEV)
                performanceMeasureEnd();
        }
        const { proto, template, dataDeepCopy, propertyPassingDeepCopy, relationDefinitionGroup } = def._$detail;
        const options = def._$options;
        const behavior = def.behavior;
        const nodeTreeContext = owner
            ? owner.getBackendContext()
            : backendContext || globalOptions.backendContext || getDefaultBackendContext();
        const external = options.externalComponent;
        const propEarlyInit = options.propertyEarlyInit;
        const writeExtraInfoToAttr = globalOptions.writeExtraInfoToAttr;
        const virtualHost = options.virtualHost;
        // initialize component instance object
        const comp = Object.create(proto);
        if (ENV.DEV)
            performanceMeasureStart('component.create', comp);
        comp._$genericImpls = genericImpls;
        comp._$external = external;
        comp.tagName = tagName;
        comp._$methodCaller = comp;
        comp.ownerShadowRoot = owner;
        // check shared style scope
        const ownerSpace = behavior.ownerSpace;
        const sharedStyleScope = ownerSpace._$sharedStyleScope;
        const [styleScope, extraStyleScope] = comp.getStyleScopes();
        const isDedicatedStyleScope = styleScope && styleScope !== sharedStyleScope;
        // create backend element
        let backendElement = null;
        if (nodeTreeContext) {
            if (BM.DOMLIKE || (BM.DYNAMIC && nodeTreeContext.mode === 3 /* BackendMode.Domlike */)) {
                if (!virtualHost) {
                    if (ENV.DEV)
                        performanceMeasureStart('backend.createElement');
                    backendElement = nodeTreeContext.document.createElement(tagName);
                    if (isDedicatedStyleScope) {
                        const styleScopePrefix = ownerSpace.styleScopeManager.queryName(styleScope);
                        if (styleScopePrefix) {
                            backendElement.setAttribute('wx-host', styleScopePrefix);
                        }
                    }
                    if (ENV.DEV)
                        performanceMeasureEnd();
                }
            }
            else if (BM.COMPOSED || (BM.DYNAMIC && nodeTreeContext.mode === 2 /* BackendMode.Composed */)) {
                if (!virtualHost) {
                    if (ENV.DEV)
                        performanceMeasureStart('backend.createElement');
                    backendElement = nodeTreeContext.createElement(options.hostNodeTagName, tagName);
                    if (ENV.DEV)
                        performanceMeasureEnd();
                }
            }
            else if (BM.SHADOW || (BM.DYNAMIC && nodeTreeContext.mode === 1 /* BackendMode.Shadow */)) {
                if (ENV.DEV)
                    performanceMeasureStart('component.createComponent');
                const sr = owner
                    ? owner._$backendShadowRoot
                    : nodeTreeContext.getRootNode();
                const be = sr.createComponent(tagName, external, virtualHost, styleScope !== null && styleScope !== void 0 ? styleScope : StyleScopeManager.globalScope(), extraStyleScope, behavior._$externalClasses, 
                // eslint-disable-next-line no-nested-ternary
                external
                    ? null
                    : // eslint-disable-next-line no-nested-ternary
                        options.dynamicSlots
                            ? 3 /* SlotMode.Dynamic */
                            : options.multipleSlots
                                ? 2 /* SlotMode.Multiple */
                                : 1 /* SlotMode.Single */, options.writeIdToDOM);
                backendElement = be;
                if (ENV.DEV)
                    performanceMeasureEnd();
            }
        }
        comp._$initialize(def.is, virtualHost, backendElement, owner, (_a = owner === null || owner === void 0 ? void 0 : owner._$nodeTreeContext) !== null && _a !== void 0 ? _a : nodeTreeContext);
        const ownerHost = owner ? owner.getHostNode() : undefined;
        // init class list
        const [ownerStyleScope, ownerExtraStyleScope, styleScopeManager] = (_b = ownerHost === null || ownerHost === void 0 ? void 0 : ownerHost.getStyleScopes()) !== null && _b !== void 0 ? _b : [null, null, comp.getOwnerSpace().styleScopeManager];
        comp.classList = new ClassList(comp, behavior._$externalClasses, ownerHost ? ownerHost.classList : null, ownerStyleScope !== null && ownerStyleScope !== void 0 ? ownerStyleScope : StyleScopeManager.globalScope(), ownerExtraStyleScope !== null && ownerExtraStyleScope !== void 0 ? ownerExtraStyleScope : undefined, styleScopeManager);
        if (backendElement) {
            const elementStyleScope = ownerStyleScope !== null && ownerStyleScope !== void 0 ? ownerStyleScope : styleScope;
            if (elementStyleScope) {
                if (BM.COMPOSED || (BM.DYNAMIC && nodeTreeContext.mode === 2 /* BackendMode.Composed */)) {
                    ;
                    backendElement.setStyleScope(elementStyleScope, ownerExtraStyleScope !== null && ownerExtraStyleScope !== void 0 ? ownerExtraStyleScope : undefined, isDedicatedStyleScope ? styleScope !== null && styleScope !== void 0 ? styleScope : StyleScopeManager.globalScope() : undefined);
                }
            }
        }
        // associate in backend
        if (backendElement) {
            if (ENV.DEV)
                performanceMeasureStart('backend.associateValue');
            backendElement.__wxElement = comp;
            if (!(BM.DOMLIKE || (BM.DYNAMIC && nodeTreeContext.mode === 3 /* BackendMode.Domlike */))) {
                ;
                backendElement.associateValue(comp);
            }
            else {
                ;
                nodeTreeContext.associateValue(backendElement, comp);
            }
            if (ENV.DEV)
                performanceMeasureEnd();
            if (styleScopeManager && writeExtraInfoToAttr) {
                const prefix = styleScopeManager.queryName(ownerStyleScope !== null && ownerStyleScope !== void 0 ? ownerStyleScope : StyleScopeManager.globalScope());
                if (prefix) {
                    backendElement.setAttribute('exparser:info-class-prefix', `${prefix}--`);
                }
            }
        }
        // create template engine
        const tmplInst = template.createInstance(comp, 
        // eslint-disable-next-line @typescript-eslint/unbound-method
        ShadowRoot.createShadowRoot);
        comp.templateInstance = tmplInst;
        // write attr
        if (writeExtraInfoToAttr && backendElement) {
            const componentInstanceId = componentInstanceIdInc;
            componentInstanceIdInc += 1;
            comp._$componentInstanceId = componentInstanceId;
            backendElement.setAttribute('exparser:info-component-id', componentInstanceId);
        }
        comp._$idPrefix = options.idPrefixGenerator
            ? options.idPrefixGenerator.call(comp)
            : '';
        // combine initial data
        const staticData = behavior._$staticData;
        const dataGenFuncs = behavior._$data;
        let data;
        if (staticData === undefined) {
            if (dataGenFuncs.length === 1) {
                const f = dataGenFuncs[0];
                data = f();
            }
            else {
                data = {};
                for (let i = 0; i < dataGenFuncs.length; i += 1) {
                    const f = dataGenFuncs[i];
                    Object.assign(data, f());
                }
            }
        }
        else {
            data = simpleDeepCopy(staticData);
            for (let i = 0; i < dataGenFuncs.length; i += 1) {
                const f = dataGenFuncs[i];
                Object.assign(data, f());
            }
        }
        // init relations
        const relation = (comp._$relation = new Relation(comp, relationDefinitionGroup));
        // init trait group
        comp._$traitGroup = new TraitGroup();
        const traitBehaviors = behavior._$traitBehaviors;
        if (traitBehaviors !== undefined) {
            for (let i = 0; i < traitBehaviors.length; i += 1) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const { traitBehavior, impl } = traitBehaviors[i];
                comp._$traitGroup.implement(traitBehavior, impl);
            }
        }
        // call method caller init
        if (behavior._$methodCallerInit) {
            const methodCaller = behavior._$methodCallerInit.call(comp);
            comp._$methodCaller = methodCaller;
        }
        // call init functions
        let initDone = false;
        function relationInit(relationDef) {
            if (initDone)
                throw new ThirdError('Cannot execute init-time functions after initialization', '[implement]', behavior.is);
            const target = relationDef.target;
            const normalizedRel = normalizeRelation(behavior.ownerSpace, behavior.is, 'undefined', relationDef);
            let key;
            if (normalizedRel) {
                key = relation.add(normalizedRel);
            }
            else {
                key = Symbol('invalid');
            }
            const list = () => {
                const targetNodes = relation.getLinkedTargets(key);
                return targetNodes.map((x) => x.getMethodCaller());
            };
            const listAsTrait = target instanceof TraitBehavior
                ? () => {
                    const targetNodes = relation.getLinkedTargets(key);
                    return targetNodes.map((x) => x.traitBehavior(target));
                }
                : undefined;
            return { list, listAsTrait };
        }
        if (behavior._$init.length > 0) {
            let cowMethodMap = true;
            const methodCaller = comp.getMethodCaller();
            const builderContext = {
                self: methodCaller,
                data,
                setData: comp.setData.bind(comp),
                implement: (traitBehavior, impl) => {
                    if (initDone)
                        throw new ThirdError('Cannot execute init-time functions after initialization', '[implement]', behavior.is);
                    comp._$traitGroup.implement(traitBehavior, impl);
                },
                relation: relationInit,
                observer: (dataPaths, func) => {
                    if (initDone)
                        throw new ThirdError('Cannot execute init-time functions after initialization', '[implement]', behavior.is);
                    comp.dynamicAddObserver(func, dataPaths);
                },
                lifetime: (name, func) => {
                    if (initDone)
                        throw new ThirdError('Cannot execute init-time functions after initialization', '[implement]', behavior.is);
                    comp.addLifetimeListener(name, func);
                },
                pageLifetime: (name, func) => {
                    if (initDone)
                        throw new ThirdError('Cannot execute init-time functions after initialization', '[implement]', behavior.is);
                    comp.addPageLifetimeListener(name, func);
                },
                method: (func) => Component._$tagMethod(func),
                listener: (func) => Component._$tagMethod(func),
            };
            const initFuncs = behavior._$init;
            for (let i = 0; i < initFuncs.length; i += 1) {
                const init = initFuncs[i].func;
                if (ENV.DEV)
                    performanceMeasureStart('component.init', comp);
                const exported = safeCallback('Component Init', init, methodCaller, [builderContext], undefined);
                if (ENV.DEV)
                    performanceMeasureEnd();
                if (exported) {
                    const exportedKeys = Object.keys(exported);
                    for (let j = 0; j < exportedKeys.length; j += 1) {
                        const exportedKey = exportedKeys[j];
                        const exportItem = exported[exportedKey];
                        if (Component.isTaggedMethod(exportItem)) {
                            if (cowMethodMap) {
                                cowMethodMap = false;
                                comp._$methodMap = Object.create(comp._$methodMap);
                            }
                            comp._$methodMap[exportedKey] = exportItem;
                            if (options.writeFieldsToNode) {
                                ;
                                comp[exportedKey] = exportItem;
                            }
                        }
                    }
                }
            }
        }
        initDone = true;
        // init data
        const shadowRoot = tmplInst.shadowRoot;
        comp.shadowRoot = shadowRoot;
        const dataGroup = new DataGroup(comp, data, options.pureDataPattern || null, dataDeepCopy, propertyPassingDeepCopy, options.reflectToAttributes, comp._$dataGroupObserverTree, options.propertyComparer, options.unknownPropertyHandler);
        comp._$dataGroup = dataGroup;
        // init template with init data
        if (propEarlyInit && initPropValues !== undefined)
            initPropValues(comp);
        if (ENV.DEV) {
            performanceMeasureRenderWaterfall('component.render', 'backend.render', comp, () => {
                tmplInst.initValues(dataGroup.innerData || dataGroup.data);
            });
        }
        else {
            tmplInst.initValues(dataGroup.innerData || dataGroup.data);
        }
        dataGroup.updateListener = (data, combinedChanges) => {
            if (ENV.DEV) {
                performanceMeasureRenderWaterfall('component.render', 'backend.render', comp, () => {
                    tmplInst.updateValues(data, combinedChanges);
                });
            }
            else {
                tmplInst.updateValues(data, combinedChanges);
            }
        };
        // bind behavior listeners
        const listeners = behavior._$listeners;
        if (listeners !== undefined) {
            for (let i = 0; i < listeners.length; i += 1) {
                const { id, ev, listener: gf } = listeners[i];
                const listener = gf;
                if (id === 'this') {
                    comp.addListener(ev, (ev) => listener.call(comp._$methodCaller, ev));
                }
                else if (!external) {
                    const sr = shadowRoot;
                    const elem = id ? sr.getElementById(id) : sr;
                    if (elem) {
                        elem.addListener(ev, (ev) => listener.call(comp._$methodCaller, ev));
                    }
                }
                else {
                    const sr = shadowRoot;
                    const elem = id ? sr.getIdMap()[id] : sr.root;
                    if (elem) {
                        sr.setListener(elem, ev, (ev) => listener.call(comp._$methodCaller, ev));
                    }
                }
            }
        }
        // trigger created lifetimes
        comp.triggerLifetime('created', []);
        if (!propEarlyInit && initPropValues !== undefined)
            initPropValues(comp);
        if (ENV.DEV)
            performanceMeasureEnd();
        return comp;
    }
    static createWithGenericsAndContext(tagName, componentDefinition, genericTargets, backendContext, initPropValues) {
        const collectGenericImpls = (def) => {
            let genericImpls;
            if (genericTargets) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                genericImpls = Object.create(null);
                Object.entries(genericTargets).forEach(([key, g]) => {
                    genericImpls[key] = {
                        final: g,
                        source: def.behavior,
                        placeholder: null,
                        waiting: null,
                    };
                });
            }
            return convertGenerics(def, def.behavior, undefined, genericImpls);
        };
        if (componentDefinition) {
            return Component._$advancedCreate(String(tagName), componentDefinition, null, backendContext, collectGenericImpls(componentDefinition), initPropValues);
        }
        if (tagName instanceof ComponentDefinition) {
            return Component._$advancedCreate(tagName.is, tagName, null, backendContext, collectGenericImpls(tagName), undefined);
        }
        const compDef = getDefaultComponentSpace().getComponentByUrl(tagName, '');
        return Component._$advancedCreate(tagName, compDef, null, backendContext, collectGenericImpls(compDef), undefined);
    }
    static createWithGenerics(tagName, componentDefinition, genericImpls, initPropValues) {
        return Component.createWithGenericsAndContext(tagName, componentDefinition, genericImpls, null, initPropValues);
    }
    static createWithContext(tagName, componentDefinition, backendContext, initPropValues) {
        return Component.createWithGenericsAndContext(tagName, componentDefinition, null, backendContext, initPropValues);
    }
    static create(tagName, componentDefinition, initPropValues) {
        return Component.createWithGenericsAndContext(tagName, componentDefinition, null, null, initPropValues);
    }
    get properties() {
        return this._$dataGroup.data;
    }
    get data() {
        return this._$dataGroup.data;
    }
    set data(newData) {
        const dataGroup = this._$dataGroup;
        if (dataGroup === undefined) {
            throw new ThirdError('Cannot update data before component created', `data setter`, this);
        }
        Object.entries(newData).forEach(([key, value]) => dataGroup.replaceDataOnPath([key], value));
        dataGroup.applyDataUpdates();
    }
    get $() {
        if (this._$external) {
            return this.shadowRoot.getIdMap();
        }
        return this.shadowRoot._$getIdMap();
    }
    /**
     * Returns the shadow root element
     *
     * Returns `null` for external components.
     */
    getShadowRoot() {
        if (this._$external) {
            return null;
        }
        return this.shadowRoot;
    }
    /**
     * Apply the template updates to this component instance
     *
     * This method throws error if the template engine does not support template update.
     */
    applyTemplateUpdates() {
        var _a;
        if (!((_a = this.templateInstance) === null || _a === void 0 ? void 0 : _a.updateTemplate)) {
            throw new Error(`The template engine of component "${this.is}" does not support template update`);
        }
        const dataGroup = this._$dataGroup;
        this.templateInstance.updateTemplate(this._$definition._$detail.template, dataGroup.innerData || dataGroup.data);
    }
    /**
     * Returns the owner component space of this component
     */
    getOwnerSpace() {
        return this._$behavior.ownerSpace;
    }
    /** Get whether the component is external or not */
    isExternal() {
        return this._$external;
    }
    static listProperties(comp) {
        return Object.keys(comp._$behavior._$propertyMap);
    }
    static hasProperty(comp, propName) {
        return !!comp._$behavior._$propertyMap[propName];
    }
    /**
     * Check whether a property has been explicitly set since the component was created.
     *
     * A property is considered "dirty" once it receives a value through an update
     * (e.g. `setData`, or parent component passing a prop).
     * A property that still holds its initial default value is not dirty.
     */
    static isDirtyProperty(comp, propName) {
        return !!comp._$dataGroup._$propDirty[propName];
    }
    /** List methods by the component definition (backward compatibility) */
    static getMethodsFromDef(compDef) {
        return compDef.behavior._$methodMap;
    }
    /**
     * Get a method
     *
     * If `useMethodCallerListeners` option is set for this component,
     * this method will use the corresponding fields in the `methodCaller` .
     */
    static getMethod(comp, methodName) {
        if (comp._$definition._$options.useMethodCallerListeners &&
            Object.prototype.hasOwnProperty.call(comp._$methodCaller, methodName)) {
            const method = comp._$methodCaller[methodName];
            return typeof method === 'function' ? method : undefined;
        }
        return comp._$methodMap[methodName];
    }
    /**
     * Call a method
     *
     * If `useMethodCallerListeners` option is set for this component,
     * this method will use the corresponding fields in the `methodCaller` .
     * Returns `undefined` if there is no such method.
     */
    callMethod(methodName, ...args) {
        const func = Component.getMethod(this, methodName);
        return func === null || func === void 0 ? void 0 : func.call(this, ...args);
    }
    /**
     * Get the corresponding component definition
     */
    getComponentDefinition() {
        return this._$definition;
    }
    /**
     * Get the options of the component
     */
    getComponentOptions() {
        return this._$definition._$options;
    }
    /**
     * Get the style scopes of the component
     */
    getStyleScopes() {
        const options = this.getComponentOptions();
        if (options.inheritStyleScope)
            return this.ownerShadowRoot
                ? this.ownerShadowRoot.getHostNode().getStyleScopes()
                : [null, null, this.getOwnerSpace().styleScopeManager];
        return [options.styleScope, options.extraStyleScope, this.getOwnerSpace().styleScopeManager];
    }
    /**
     * Check whether the `other` behavior is a dependent behavior or a implemented trait behavior
     */
    hasBehavior(other) {
        if (other instanceof TraitBehavior) {
            return this._$traitGroup.get(other) !== undefined;
        }
        return this._$behavior.hasBehavior(other);
    }
    /** Get the root behavior of the component */
    getRootBehavior() {
        return this._$behavior;
    }
    /**
     * Get the trait behavior implementation of the component
     *
     * Returns `undefined` if the specified trait behavior is not implemented.
     */
    traitBehavior(traitBehavior) {
        return this._$traitGroup.get(traitBehavior);
    }
    /**
     * Set the caller (a.k.a. `this` ) of event callbacks and life-time callbacks
     *
     * By default, the caller is the component instance itself.
     * Use this method to override this behavior.
     */
    setMethodCaller(caller) {
        this._$methodCaller = caller;
    }
    /**
     * Get the current caller set by `setMethodCaller`
     */
    getMethodCaller() {
        return this._$methodCaller;
    }
    /**
     * Add a lifetime event listener on the component
     */
    addLifetimeListener(name, func) {
        if (!Object.prototype.hasOwnProperty.call(this, '_$lifetimeFuncs')) {
            // copy on write
            this._$lifetimeFuncs = this._$behavior._$getAllLifetimeFuncs();
        }
        const fag = this._$lifetimeFuncs;
        const fa = (fag[name] = fag[name] || new FuncArr('lifetime'));
        fa.add(func);
    }
    /**
     * remove a lifetime event listener on the component
     */
    removeLifetimeListener(name, func) {
        if (!Object.prototype.hasOwnProperty.call(this, '_$lifetimeFuncs')) {
            // copy on write
            this._$lifetimeFuncs = this._$behavior._$getAllLifetimeFuncs();
        }
        const fag = this._$lifetimeFuncs;
        const fa = fag[name];
        fa === null || fa === void 0 ? void 0 : fa.remove(func);
    }
    /**
     * Triggers a life-time callback on an element
     *
     * Normally external life-times should only be triggered by template engine.
     * Most cases should take a common method instead.
     */
    triggerLifetime(name, args) {
        const f = this._$lifetimeFuncs[name];
        if (f)
            f.call(this._$methodCaller, args, this);
    }
    /**
     * @internal
     * @deprecated Rename to `triggerLifetime`
     */
    triggerLifeTime(name, args) {
        return this.triggerLifetime(name, args);
    }
    /**
     * Add a page lifetime event listener on the component
     */
    addPageLifetimeListener(name, func) {
        if (!Object.prototype.hasOwnProperty.call(this, '_$pageLifetimeFuncs')) {
            // copy on write
            this._$pageLifetimeFuncs = this._$behavior._$getAllPageLifetimeFuncs();
        }
        const fag = this._$pageLifetimeFuncs;
        const fa = (fag[name] = fag[name] || new FuncArr('pageLifetime'));
        fa.add(func);
    }
    /**
     * remove a page lifetime event listener on the component
     */
    removePageLifetimeListener(name, func) {
        if (!Object.prototype.hasOwnProperty.call(this, '_$pageLifetimeFuncs')) {
            // copy on write
            this._$pageLifetimeFuncs = this._$behavior._$getAllPageLifetimeFuncs();
        }
        const fag = this._$pageLifetimeFuncs;
        const fa = fag[name];
        fa === null || fa === void 0 ? void 0 : fa.remove(func);
    }
    /**
     * Triggers a page-life-time callback on an element
     */
    triggerPageLifetime(name, args) {
        const rec = (node) => {
            if (isComponent(node)) {
                if (node._$pageLifetimeFuncs) {
                    const f = node._$pageLifetimeFuncs[name];
                    if (f)
                        f.call(node._$methodCaller, args, this);
                }
                if (!node._$external)
                    rec(node.shadowRoot);
            }
            const children = node.childNodes;
            for (let i = 0; i < children.length; i += 1) {
                const child = children[i];
                if (isElement(child)) {
                    rec(child);
                }
            }
        };
        rec(this);
    }
    /**
     * @internal
     * @deprecated Rename to `triggerPageLifetime`
     */
    triggerPageLifeTime(name, args) {
        return this.triggerPageLifetime(name, args);
    }
    /**
     * Add an observer on the runtime
     * @note This method is for debug or inspect use only, do not use it in production.
     */
    dynamicAddObserver(func, dataPaths) {
        if (!Object.prototype.hasOwnProperty.call(this, '_$dataGroupObserverTree')) {
            // copy on write
            this._$dataGroupObserverTree = this._$dataGroupObserverTree.cloneSub();
            if (this._$dataGroup)
                this._$dataGroup._$observerTree = this._$dataGroupObserverTree;
        }
        const dataGroupObserverTree = this._$dataGroupObserverTree;
        try {
            dataGroupObserverTree.addObserver(func, parseMultiPaths(dataPaths));
        }
        catch (e) {
            // parse multi paths may throw errors
            dispatchError(e, `observer`, this.is);
        }
    }
    /**
     * Get the target elements of a relation
     */
    getRelationNodes(relationKey) {
        var _a;
        return ((_a = this._$relation) === null || _a === void 0 ? void 0 : _a.getLinkedTargets(relationKey)) || [];
    }
    /** Check the existence of an external class */
    hasExternalClass(name) {
        return this.classList._$hasAlias(name);
    }
    /** Update an external class value */
    setExternalClass(name, target) {
        this.scheduleExternalClassChange(name, target);
        this.applyExternalClassChanges();
        if (this._$mutationObserverTarget) {
            MutationObserverTarget.callAttrObservers(this, {
                type: 'properties',
                target: this,
                nameType: 'external-class',
                attributeName: name,
            });
        }
    }
    /** Get all external classes */
    getExternalClasses() {
        return this.classList._$getAlias();
    }
    /** Schedule an update for an external class value */
    scheduleExternalClassChange(name, target) {
        this.classList._$setAlias(name, target);
    }
    /** Update multiple external class values */
    applyExternalClassChanges() {
        if (this._$external)
            return;
        if (!this.classList._$shouldUpdateExternalClass())
            return;
        const recv = (node) => {
            if (!isElement(node))
                return;
            const classList = node.classList;
            if ((classList === null || classList === void 0 ? void 0 : classList._$spreadExternalClassUpdate()) && isComponent(node)) {
                node.applyExternalClassChanges();
            }
            const childNodes = node.childNodes;
            for (let i = 0, l = childNodes.length; i < l; i += 1) {
                recv(childNodes[i]);
            }
            classList === null || classList === void 0 ? void 0 : classList._$markExternalClassUpdated();
        };
        recv(this.shadowRoot);
    }
    /** Triggers a worklet change lifetime */
    triggerWorkletChangeLifetime(name, value) {
        this.triggerLifetime('workletChange', [name, value]);
    }
    /** Check a field is excluded by pureDataPattern or not */
    isInnerDataExcluded(fieldName) {
        var _a;
        return ((_a = this._$definition._$options.pureDataPattern) === null || _a === void 0 ? void 0 : _a.test(fieldName)) || false;
    }
    static getInnerData(comp) {
        return comp._$dataGroup.innerData;
    }
    static getDataProxy(comp) {
        return comp._$dataGroup;
    }
    static replaceWholeData(comp, newData) {
        comp._$dataGroup.replaceWholeData(newData);
    }
    replaceDataOnPath(path, data) {
        const dataProxy = this._$dataGroup;
        if (dataProxy === undefined) {
            throw new ThirdError('Cannot update data before component created', `replaceDataOnPath`, this);
        }
        if (ENV.DEV)
            performanceMeasureStart('component.replaceDataOnPath', this);
        dataProxy.replaceDataOnPath(path, data);
        if (ENV.DEV)
            performanceMeasureEnd();
    }
    spliceArrayDataOnPath(path, index, del, inserts) {
        const dataProxy = this._$dataGroup;
        if (dataProxy === undefined) {
            throw new ThirdError('Cannot update data before component created', `spliceArrayDataOnPath`, this);
        }
        if (ENV.DEV)
            performanceMeasureStart('component.spliceArrayDataOnPath', this);
        dataProxy.spliceArrayDataOnPath(path, index, del, inserts);
        if (ENV.DEV)
            performanceMeasureEnd();
    }
    /**
     * Check whether there are pending changes or not
     */
    hasPendingChanges() {
        const dataProxy = this._$dataGroup;
        if (dataProxy === undefined)
            return false;
        return dataProxy.getChanges().length > 0;
    }
    /**
     * Apply all scheduled updates immediately
     *
     * Inside observers, it is generally not .
     */
    applyDataUpdates() {
        const dataProxy = this._$dataGroup;
        if (dataProxy === undefined) {
            throw new ThirdError('Cannot update data before component created', `applyDataUpdates`, this);
        }
        if (ENV.DEV)
            performanceMeasureStart('component.applyDataUpdates', this);
        dataProxy.applyDataUpdates();
        if (ENV.DEV)
            performanceMeasureEnd();
    }
    /**
     * Pending all data updates in the callback, and apply updates after callback returns
     *
     * This function helps grouping several `replaceDataOnPath` or `spliceArrayDataOnPath` calls,
     * and then apply them at the end of the callback.
     * `setData` and `applyDataUpdates` calls inside the callback still apply updates immediately.
     */
    groupUpdates(callback) {
        const dataProxy = this._$dataGroup;
        if (dataProxy === undefined) {
            throw new ThirdError('Cannot update data before component created', `groupUpdates`, this);
        }
        if (ENV.DEV)
            performanceMeasureStart('component.groupUpdates', this);
        const ret = callback();
        dataProxy.applyDataUpdates();
        if (ENV.DEV)
            performanceMeasureEnd();
        return ret;
    }
    updateData(newData) {
        const dataProxy = this._$dataGroup;
        if (dataProxy === undefined) {
            throw new ThirdError('Cannot update data before component created', `updateData`, this);
        }
        if (ENV.DEV)
            performanceMeasureStart('component.updateData', this);
        if (typeof newData === 'object' && newData !== null) {
            const keys = Object.keys(newData);
            for (let i = 0; i < keys.length; i += 1) {
                const key = keys[i];
                try {
                    const p = parseSinglePath(key);
                    dataProxy.replaceDataOnPath(p, newData[key]);
                }
                catch (e) {
                    // parse single path may throw errors
                    dispatchError(e, `updateData`, this);
                }
            }
        }
        if (ENV.DEV)
            performanceMeasureEnd();
    }
    setData(newData) {
        const dataProxy = this._$dataGroup;
        if (dataProxy === undefined) {
            throw new ThirdError('Cannot update data before component created', `setData`, this);
        }
        if (ENV.DEV)
            performanceMeasureStart('component.setData', this);
        if (typeof newData === 'object' && newData !== null) {
            const keys = Object.keys(newData);
            for (let i = 0; i < keys.length; i += 1) {
                const key = keys[i];
                try {
                    const p = parseSinglePath(key);
                    dataProxy.replaceDataOnPath(p, newData[key]);
                }
                catch (e) {
                    // parse single path may throw errors
                    dispatchError(e, `setData`, this);
                }
            }
        }
        dataProxy.applyDataUpdates();
        if (ENV.DEV)
            performanceMeasureEnd();
    }
}
Component.isComponent = isComponent;
Component.prototype[COMPONENT_SYMBOL] = true;
//# sourceMappingURL=component.js.map